// ==================== SUBSCRIPTIONS PAGE (ADMIN) ====================

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, 
  Users,
  Calendar,
  DollarSign,
  AlertCircle,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  PauseCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { RenewalAmounts } from '../../components/coupons/RenewalAmounts';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { fadeInUp, staggerContainer } from '../../utils/animations';
import { 
  subscriptionsService, 
  type Subscription, 
  type SubscriptionStatistics 
} from '../../services';

// ==================== STAT CARD COMPONENT ====================

interface StatCardProps {
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  trend?: string;
  trendUp?: boolean;
  caption?: string;
}

const StatCard = ({ title, value, icon, trend, trendUp, caption }: StatCardProps) => (
  <motion.div variants={fadeInUp}>
    <Card className="relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-(--color-muted-gray)">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {caption && <p className="text-xs text-(--color-muted-gray) mt-2">{caption}</p>}
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trendUp ? 'text-(--color-success)' : 'text-(--color-error)'}`}>
              {trend}
            </div>
          )}
        </div>
        <div className="p-3 rounded-xl bg-(--color-navy-light) text-(--color-signal-orange)">
          {icon}
        </div>
      </div>
    </Card>
  </motion.div>
);

// ==================== STATUS BADGE COMPONENT ====================

const StatusBadge = ({ status }: { status: string }) => {
  const config = {
    PENDING: { variant: 'info' as const, icon: <Clock size={12} />, label: 'Pending' },
    ACTIVE: { variant: 'success' as const, icon: <CheckCircle size={12} />, label: 'Active' },
    PAST_DUE: { variant: 'warning' as const, icon: <AlertTriangle size={12} />, label: 'Past Due' },
    CANCELLED: { variant: 'error' as const, icon: <XCircle size={12} />, label: 'Cancelled' },
    EXPIRED: { variant: 'default' as const, icon: <PauseCircle size={12} />, label: 'Expired' },
  };

  const { variant, icon, label } = config[status as keyof typeof config] || config.PENDING;

  return (
    <Badge variant={variant} className="flex items-center gap-1">
      {icon}
      {label}
    </Badge>
  );
};

// ==================== SUBSCRIPTION ROW COMPONENT ====================

interface SubscriptionRowProps {
  subscription: Subscription;
}

const SubscriptionRow = ({ subscription }: SubscriptionRowProps) => {
  // Get amount from plan pricing based on billing cycle
  const getPlanAmount = () => {
    const plan = subscription.plan;
    if (subscription.billingCycle === 'YEARLY' && plan.yearlyPrice) {
      return parseFloat(plan.yearlyPrice) || 0;
    }
    return parseFloat(plan.monthlyPrice) || 0;
  };

  const amount = getPlanAmount();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const organization = subscription.organizations[0];

  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="border-b border-(--color-border-gray) hover:bg-(--color-navy-light)/50 transition-colors"
    >
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-(--color-signal-orange)/10 flex items-center justify-center">
            <Building2 size={20} className="text-(--color-signal-orange)" />
          </div>
          <div>
            <p className="font-medium text-white">
              {organization?.businessName || 'No Organization'}
            </p>
            <p className="text-sm text-(--color-muted-gray)">
              {organization?.businessEmail || '-'}
            </p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <Badge variant="orange">{subscription.plan.name}</Badge>
        {subscription.coupon && (
          <Badge variant="success" size="sm" className="ml-2">
            {subscription.coupon.code} −{Number(subscription.coupon.discountPercent)}%
          </Badge>
        )}
      </td>
      <td className="py-4 px-4">
        <StatusBadge status={subscription.status} />
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-1 text-white">
          <DollarSign size={14} className="text-(--color-muted-gray)" />
          <span className="font-medium">{amount.toFixed(2)}</span>
          <span className="text-(--color-muted-gray) text-sm">
            /{subscription.billingCycle === 'MONTHLY' ? 'mo' : 'yr'}
          </span>
        </div>
      </td>
      <td className="py-4 px-4">
        <Badge variant={subscription.billingCycle === 'YEARLY' ? 'success' : 'default'}>
          {subscription.billingCycle}
        </Badge>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-1 text-(--color-muted-gray)">
          <Clock size={14} />
          <span className="text-sm">{formatDate(subscription.currentPeriodEnd)}</span>
        </div>
      </td>
    </motion.tr>
  );
};

// ==================== MAIN COMPONENT ====================

const SubscriptionsPage = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [statistics, setStatistics] = useState<SubscriptionStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Fetch subscriptions
  const fetchSubscriptions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await subscriptionsService.getAll({
        page,
        limit,
        status: statusFilter || undefined,
        search: search || undefined,
      });

      if (response.success && response.data) {
        // Handle paginated response
        if ('data' in response.data && Array.isArray(response.data.data)) {
          setSubscriptions(response.data.data);
          setTotalPages(response.data.meta?.totalPages || 1);
          setTotal(response.data.meta?.total || 0);
        } else if (Array.isArray(response.data)) {
          setSubscriptions(response.data as unknown as Subscription[]);
          setTotalPages(1);
          setTotal((response.data as unknown as Subscription[]).length);
        }
      } else {
        setError(response.message || 'Failed to load subscriptions');
        setSubscriptions([]);
      }
    } catch {
      setError('Failed to load subscriptions');
      setSubscriptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, statusFilter, search]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await subscriptionsService.getStatistics();
      if (response.success && response.data) {
        setStatistics(response.data);
      }
    } catch {
      // Silently fail for statistics
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
    fetchStatistics();
  }, [fetchSubscriptions, fetchStatistics]);

  // Handle search
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Subscriptions</h1>
          <p className="text-(--color-muted-gray) mt-1">
            Manage customer subscriptions and billing
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            fetchSubscriptions();
            fetchStatistics();
          }}
          leftIcon={<RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </motion.div>

      {/* Statistics Cards */}
      {statistics && (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <StatCard
            title="Total Subscriptions"
            value={statistics.total}
            icon={<CreditCard size={24} />}
          />
          <StatCard
            title="Active"
            value={statistics.active}
            icon={<CheckCircle size={24} />}
          />
          <StatCard
            title="Monthly MRR"
            caption="Expected renewal charges after recurring discounts"
            value={<RenewalAmounts amounts={statistics.monthlyRecurringRevenueByCurrency} fallback={statistics.monthlyRecurringRevenue} />}
            icon={<DollarSign size={24} />}
          />
          <StatCard
            title="Billing Split"
            value={`${statistics.monthlySubscriptions}M / ${statistics.yearlySubscriptions}Y`}
            icon={<Calendar size={24} />}
          />
        </motion.div>
      )}

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-xl bg-(--color-error)/10 border border-(--color-error)/30"
          >
            <AlertCircle size={20} className="text-(--color-error)" />
            <p className="text-sm text-(--color-error) flex-1">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <motion.div variants={fadeInUp}>
        <Card>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="flex gap-2">
                <Input
                  placeholder="Search by organization name or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  leftIcon={<Search size={16} />}
                />
                <Button variant="secondary" onClick={handleSearch}>
                  Search
                </Button>
              </div>
            </div>
            <div className="w-full md:w-48">
              <Select
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'PENDING', label: 'Pending' },
                  { value: 'ACTIVE', label: 'Active' },
                  { value: 'PAST_DUE', label: 'Past Due' },
                  { value: 'EXPIRED', label: 'Expired' },
                  { value: 'CANCELLED', label: 'Cancelled' },
                ]}
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value as string);
                  setPage(1);
                }}
                placeholder="Filter by status"
              />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Subscriptions Table */}
      <motion.div variants={fadeInUp}>
        <Card className="overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={32} className="text-(--color-signal-orange) animate-spin" />
            </div>
          ) : !subscriptions || subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <Users size={48} className="text-(--color-muted-gray) mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Subscriptions Found</h3>
              <p className="text-(--color-muted-gray)">
                {search || statusFilter
                  ? 'Try adjusting your filters'
                  : 'No subscriptions have been created yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-(--color-border-gray)">
                      <th className="text-left py-3 px-4 text-sm font-medium text-(--color-muted-gray)">
                        Organization
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-(--color-muted-gray)">
                        Plan
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-(--color-muted-gray)">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-(--color-muted-gray)">
                        Amount
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-(--color-muted-gray)">
                        Billing
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-(--color-muted-gray)">
                        Next Renewal
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((subscription) => (
                      <SubscriptionRow
                        key={subscription.id}
                        subscription={subscription}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-(--color-border-gray)">
                  <p className="text-sm text-(--color-muted-gray)">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} subscriptions
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      leftIcon={<ChevronLeft size={16} />}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-(--color-soft-gray)">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      rightIcon={<ChevronRight size={16} />}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </motion.div>
    </motion.div>
  );
};

export { SubscriptionsPage };

